import type { PatrolPeriodIso } from "./normalizePatrolWindow";

export type ResolvedPatrolDisplay = { displayStartIso: string; displayEndIso: string };

/** Do not treat sub-minute API spans as a real patrol window. */
const MIN_WINDOW_MS = 60_000;
/** Only infer "first period from clock-in" within this many patrol intervals after shift start. */
const BOOTSTRAP_INTERVAL_MULTIPLIER = 2;

/**
 * Deadline and displayed time range for patrol urgency. When the server returns a collapsed window
 * or a deadline at/before shift start (common right after clock-in), the first obligation period is
 * [shift start → shift start + interval] using site settings.
 */
export function resolvePatrolEffectiveWindow(options: {
  normalized: PatrolPeriodIso | null;
  openShift: { started_at: string } | null | undefined;
  intervalMinutes: number | null | undefined;
  resolvedDisplay: ResolvedPatrolDisplay | null;
  fallbackStartIso: string;
  fallbackDeadlineIso: string;
  nowMs?: number;
}): {
  deadlineIso: string;
  displayStartIso: string;
  displayEndIso: string;
} {
  const {
    normalized,
    openShift,
    intervalMinutes,
    resolvedDisplay,
    fallbackStartIso,
    fallbackDeadlineIso,
    nowMs = Date.now(),
  } = options;

  const interval = intervalMinutes ?? 0;
  const defaultDisplayStart = resolvedDisplay?.displayStartIso || fallbackStartIso;
  const defaultDisplayEnd = resolvedDisplay?.displayEndIso || fallbackDeadlineIso;
  const defaultDeadline = normalized?.periodEndIso || fallbackDeadlineIso;

  if (!openShift || interval <= 0 || !normalized?.periodEndIso || !normalized.periodStartIso) {
    return {
      deadlineIso: defaultDeadline,
      displayStartIso: defaultDisplayStart,
      displayEndIso: defaultDisplayEnd,
    };
  }

  const shiftMs = new Date(openShift.started_at).getTime();
  const apiEndMs = new Date(normalized.periodEndIso).getTime();
  const apiStartMs = new Date(normalized.periodStartIso).getTime();
  if (Number.isNaN(shiftMs) || Number.isNaN(apiEndMs) || Number.isNaN(apiStartMs)) {
    return {
      deadlineIso: defaultDeadline,
      displayStartIso: defaultDisplayStart,
      displayEndIso: defaultDisplayEnd,
    };
  }

  const apiDuration = apiEndMs - apiStartMs;
  const degenerate = apiDuration < MIN_WINDOW_MS;
  const withinBootstrap =
    nowMs - shiftMs <= BOOTSTRAP_INTERVAL_MULTIPLIER * interval * 60_000;
  const deadlineAtOrBeforeShift = apiEndMs <= shiftMs + 2000;
  const apiEndNearShiftInstant =
    degenerate && Math.abs(apiEndMs - shiftMs) < MIN_WINDOW_MS;

  if (withinBootstrap && (degenerate || deadlineAtOrBeforeShift || apiEndNearShiftInstant)) {
    const endMs = shiftMs + interval * 60_000;
    const endIso = new Date(endMs).toISOString();
    return {
      deadlineIso: endIso,
      displayStartIso: openShift.started_at,
      displayEndIso: endIso,
    };
  }

  return {
    deadlineIso: normalized.periodEndIso,
    displayStartIso: defaultDisplayStart,
    displayEndIso: defaultDisplayEnd,
  };
}

/** Last portion of the patrol interval where "due soon" (orange) UI activates. */
export const PATROL_IMMINENT_FRACTION = 0.25;

export function getPatrolImminentThresholdMs(patrolIntervalMinutes: number): number | null {
  if (patrolIntervalMinutes <= 0) return null;
  return PATROL_IMMINENT_FRACTION * patrolIntervalMinutes * 60_000;
}
